// src/utils/bufferingIndicator.ts
//
// Visible "Buffering…" overlay for <video> elements.
//
// PURPOSE
//   videoAuto.ts already retries playback on `waiting`/`stalled`/`suspend`
//   via scheduleManagedPlayback() — but there's no UI to tell the user a
//   stall is happening. On a slow connection the reel just sits silently
//   paused until the retry loop catches up.
//
//   This module adds the UI layer ONLY. It does not attempt to play the
//   video, call .load(), or interfere with the existing retry system —
//   it just shows/hides a spinner overlay based on the same events
//   videoAuto.ts is already listening to.
//
//   Show:  on `waiting` / `stalled` / `suspend` (after a 500ms debounce
//          so transient stalls under half a second don't flicker).
//   Hide:  on `playing` / `canplay` / `canplaythrough`.
//   Stalled escalation: if buffering persists past ~10s, swap the spinner
//   for a "Connection paused" message — videoAuto's retry timers will
//   still be firing in the background, but the user gets a clearer signal
//   that something's wrong.

const SHOW_DELAY_MS = 500;     // debounce — brief stalls don't flicker
const STALLED_AFTER_MS = 10000; // escalate to "Connection paused" after 10s
const STYLE_TAG_ID = "sc-buffering-styles";

interface State {
  overlay: HTMLElement | null;
  showTimer: number | null;
  stallTimer: number | null;
  bufferingSince: number;
}

/** Inject the stylesheet once on first use. */
function ensureStyles(): void {
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_TAG_ID;
  style.textContent = `
    @keyframes sc-buffering-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes sc-buffering-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    /* Small centered pill (NOT full-screen overlay) — HUD button stack on
       the right has no explicit position/z-index, so a full inset:0 overlay
       draws on top of it. The pill is small, positioned center, and leaves
       all sides clear so the HUD remains fully visible & interactive. */
    .sc-buffering-overlay {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 9px 14px 9px 12px;
      background: rgba(8, 12, 10, 0.78);
      color: #fff;
      pointer-events: none;
      z-index: 900;
      border-radius: 999px;
      box-shadow: 0 6px 22px rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      animation: sc-buffering-fade-in 200ms ease-out forwards;
      max-width: calc(100% - 96px); /* leave room for HUD column on the right */
    }
    .sc-buffering-spinner {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.25);
      border-top-color: #fff;
      animation: sc-buffering-spin 0.9s linear infinite;
    }
    .sc-buffering-text {
      font-size: 0.82rem;
      font-weight: 600;
      letter-spacing: 0.3px;
      line-height: 1;
      white-space: nowrap;
    }
    .sc-buffering-overlay.is-stalled {
      background: rgba(112, 60, 12, 0.88);
    }
    .sc-buffering-overlay.is-stalled .sc-buffering-spinner {
      border-top-color: #ffd166;
    }
    @media (prefers-reduced-motion: reduce) {
      .sc-buffering-spinner { animation-duration: 2.5s; }
      .sc-buffering-overlay { animation: none; }
    }
  `;
  document.head.appendChild(style);
}

/** Pick the closest visible container for the overlay. Prefers a reel frame
 *  (matches the clipped video bounds exactly); falls back to the immediate
 *  parent of the video. */
function pickHost(video: HTMLVideoElement): HTMLElement {
  const frame = video.closest<HTMLElement>(".video-reel-frame, .sc-media-wrap, .sc-video-wrap");
  return frame ?? video.parentElement ?? document.body;
}

function buildOverlay(): HTMLElement {
  const overlay = document.createElement("div");
  overlay.className = "sc-buffering-overlay";
  overlay.setAttribute("role", "status");
  overlay.setAttribute("aria-live", "polite");
  overlay.innerHTML =
    '<div class="sc-buffering-spinner" aria-hidden="true"></div>' +
    '<div class="sc-buffering-text">Buffering…</div>';
  return overlay;
}

/**
 * Attach a buffering indicator to a video. Idempotent — duplicate calls
 * on the same element are no-ops via the `__scBufferingAttached` flag.
 * Returns nothing; cleanup is available at `video.__scBufferingCleanup()`.
 */
export function ensureBufferingIndicator(video: HTMLVideoElement): void {
  if ((video as any).__scBufferingAttached) return;
  (video as any).__scBufferingAttached = true;
  ensureStyles();

  const state: State = {
    overlay: null,
    showTimer: null,
    stallTimer: null,
    bufferingSince: 0,
  };

  const showOverlay = (): void => {
    const host = pickHost(video);
    if (!state.overlay) {
      state.overlay = buildOverlay();
      host.appendChild(state.overlay);
    }
  };

  const escalateToStalled = (): void => {
    if (!state.overlay) return;
    state.overlay.classList.add("is-stalled");
    const txt = state.overlay.querySelector<HTMLElement>(".sc-buffering-text");
    if (txt) txt.textContent = "Connection paused — retrying";
  };

  const hideOverlay = (): void => {
    if (state.showTimer != null) {
      clearTimeout(state.showTimer);
      state.showTimer = null;
    }
    if (state.stallTimer != null) {
      clearTimeout(state.stallTimer);
      state.stallTimer = null;
    }
    if (state.overlay) {
      state.overlay.remove();
      state.overlay = null;
    }
    state.bufferingSince = 0;
  };

  const onStall = (): void => {
    if (state.showTimer != null || state.overlay) return;
    // Don't show the buffering UI if the video is intentionally paused —
    // it's only meaningful when playback is actively trying to advance.
    // This catches the "video is paused waiting for user gesture / pre-
    // active-section" case where waiting events can fire but a buffering
    // overlay would just be visual noise (and would also cover the HUD).
    if (video.paused) return;
    state.bufferingSince = Date.now();
    state.showTimer = window.setTimeout(() => {
      state.showTimer = null;
      // Re-check paused state after debounce — user may have paused during the wait
      if (video.paused) return;
      // Only show if still actually buffering (readyState below HAVE_FUTURE_DATA
      // means we don't have enough data to keep playing)
      if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
        showOverlay();
        // After the stalled threshold, escalate the message
        state.stallTimer = window.setTimeout(escalateToStalled, STALLED_AFTER_MS - SHOW_DELAY_MS);
      }
    }, SHOW_DELAY_MS);
  };

  const onResume = (): void => {
    hideOverlay();
  };

  video.addEventListener("waiting", onStall);
  video.addEventListener("stalled", onStall);
  video.addEventListener("suspend", onStall);
  video.addEventListener("playing", onResume);
  video.addEventListener("canplay", onResume);
  video.addEventListener("canplaythrough", onResume);
  video.addEventListener("pause", onResume);   // pausing should clear any buffering UI
  video.addEventListener("ended", onResume);   // and so should a finished playback
  video.addEventListener("error", () => {
    // Only show the error state if we were trying to play
    if (!video.paused) {
      showOverlay();
      escalateToStalled();
    }
  });

  const cleanup = (): void => {
    hideOverlay();
    video.removeEventListener("waiting", onStall);
    video.removeEventListener("stalled", onStall);
    video.removeEventListener("suspend", onStall);
    video.removeEventListener("playing", onResume);
    video.removeEventListener("canplay", onResume);
    video.removeEventListener("canplaythrough", onResume);
    video.removeEventListener("pause", onResume);
    video.removeEventListener("ended", onResume);
    (video as any).__scBufferingAttached = false;
  };

  (video as any).__scBufferingCleanup = cleanup;
}
